import { useMediaQuery } from "@educaplay/core";
import { useEffect } from "react";

/**
 * Checks wether "elem" has vertical scrollbar
 * @param {Element} elem HTML Element to test
 * @returns true if element visible height differs from element actual height and has overflowY != hidden, false otherwise
 */
function hasScrollbar(elem) {
  const hasVerticalScrollContent = elem.scrollHeight > elem.clientHeight;

  const overflowYStyle = window.getComputedStyle(elem)?.overflowY;

  const isOverflowYHidden = overflowYStyle?.indexOf("hidden") !== -1;

  return hasVerticalScrollContent && !isOverflowYHidden;
}

/**
 * Retrieve the element chain from 2 elements
 * @param {*} param upper and lower elements of the parent's chain
 * @returns {Element[]} Parent to Child element chain, returns an empty array otherwise
 */
function getParentescoDom({ parent, child }) {
  const aux = recursiveDom({ parent, child });
  return aux.find((elem) => elem == parent) ? aux : [];
}

/**
 * Recursive inner method for parent chain
 * @param {*} param upper and lower elements of the chain
 * @returns Parent to Child element chain
 */
function recursiveDom({ parent, child }) {
  if (!parent || !child) return [];
  if (parent == child) {
    return [child];
  }

  return (
    getParentescoDom({ parent, child: child.parentElement }).concat(child) ?? []
  );
}

/**
 * Hook for substituting vertical scroll for horizontal without the need of shiftKey
 * @param {Ref} scrollableRef Element ref that has horizontal scroll
 * @param {Ref} listenerRef Optional, element ref that will throw the scroll event. Defaults to scrollableRef if not given
 */
export default function useHorizontalScroll(
  scrollableRef,
  listenerRef,
  forceIgnore
) {
  const horizontalLayout = useMediaQuery("(orientation: landscape)");

  useEffect(() => {
    if (forceIgnore) return;
    const scrollElement = scrollableRef?.current;
    const listenElement = listenerRef?.current ?? scrollElement;

    if (!scrollElement) return;

    const scrollHandler = (evt) => {
      if (evt.shiftKey) return;
      const parentesco = getParentescoDom({
        parent: scrollElement,
        child: evt.srcElement,
      });

      const shouldScrollHorizontally = !parentesco.some((element) =>
        hasScrollbar(element)
      );

      if (shouldScrollHorizontally) {
        evt.preventDefault();

        scrollElement.scrollTo({
          left: scrollElement.scrollLeft + evt.deltaY,
          behavior: "instant",
        });
      }
    };

    if (horizontalLayout) {
      listenElement.addEventListener("mousewheel", scrollHandler);
      return () => {
        listenElement.removeEventListener("mousewheel", scrollHandler);
      };
    }
  }, [horizontalLayout, listenerRef, scrollableRef, forceIgnore]);
}
